/**
 * Payment step — Stripe Payment Element + create-booking call.
 *
 * Loads Stripe.js dynamically from https://js.stripe.com/v3/ (do NOT
 * bundle Stripe). Uses the deferred-PaymentIntent flow so Payment Element
 * can mount before we hit our backend:
 *   1. Mount Payment Element with { mode: 'payment', amount, currency }.
 *   2. On submit → elements.submit() validates fields.
 *   3. POST /create-booking → returns { stripeClientSecret, ... }.
 *   4. stripe.confirmPayment({ elements, clientSecret, redirect: 'if_required' }).
 *   5. On success → onSuccess(); on failure → display error, remain on step.
 *
 * Payment Element auto-shows Apple Pay / Google Pay / Link when the
 * browser + Stripe dashboard support them, and hides the postal code
 * field for methods that don't need it (no more US-style ZIP prompt).
 *
 * The booking is created BEFORE payment confirmation. The server marks the
 * booking as 'pending' until the Stripe webhook confirms the PaymentIntent.
 */

import type { BookingAPI, CreateBookingResponse } from '../api.js';
import { APIError } from '../api.js';
import { formatPence } from '../pricing.js';
import { escapeHTML } from '../utils/formatting.js';
import {
  formatHumanDate,
  formatLongDate,
  fromISODate,
  diffDays,
} from '../utils/dates.js';
import type { CalendarState } from './Calendar.js';
import type { GuestFormState } from './GuestForm.js';

const STRIPE_JS_URL = 'https://js.stripe.com/v3/';

interface StripeJsGlobal {
  (publishableKey: string): StripeInstance;
}

interface StripeInstance {
  elements(options?: Record<string, unknown>): StripeElements;
  confirmPayment(args: {
    elements: StripeElements;
    clientSecret: string;
    confirmParams?: Record<string, unknown>;
    redirect?: 'always' | 'if_required';
  }): Promise<{
    paymentIntent?: { status: string; id: string };
    error?: { message?: string; type?: string };
  }>;
}

interface StripeElements {
  create(type: 'payment', options?: Record<string, unknown>): StripeElement;
  submit(): Promise<{ error?: { message?: string; type?: string } }>;
  update(options: Record<string, unknown>): void;
}

interface StripeElement {
  mount(target: HTMLElement | string): void;
  unmount(): void;
  on(event: string, handler: (ev: { error?: { message?: string }; complete?: boolean; empty?: boolean }) => void): void;
  destroy(): void;
}

declare global {
  interface Window {
    Stripe?: StripeJsGlobal;
  }
}

let stripeJsPromise: Promise<StripeJsGlobal> | null = null;

function loadStripeJs(): Promise<StripeJsGlobal> {
  if (typeof window !== 'undefined' && window.Stripe) {
    return Promise.resolve(window.Stripe);
  }
  if (stripeJsPromise) return stripeJsPromise;
  stripeJsPromise = new Promise<StripeJsGlobal>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-no4w-stripe]',
    );
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = STRIPE_JS_URL;
      script.async = true;
      script.dataset.no4wStripe = 'true';
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => {
      if (window.Stripe) resolve(window.Stripe);
      else reject(new Error('Stripe.js loaded but window.Stripe is missing.'));
    });
    script.addEventListener('error', () => {
      stripeJsPromise = null;
      reject(new Error('Failed to load Stripe.js. Please check your connection and try again.'));
    });
  });
  return stripeJsPromise;
}

export interface PaymentStepOptions {
  api: BookingAPI;
  stripePublishableKey: string;
  calendar: CalendarState;
  guest: GuestFormState;
  childAges: string[];
  hasPets: boolean;
  onSuccess: (result: CreateBookingResponse) => void;
  onCancel: () => void;
  /** Called when the server reports the chosen dates just got taken (HTTP 409). */
  onDatesUnavailable?: (message: string) => void;
}

export class PaymentStep {
  private readonly host: HTMLElement;
  private readonly opts: PaymentStepOptions;
  private stripe: StripeInstance | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripeElement | null = null;
  private paymentError: string | null = null;
  private submitting = false;
  private generalError: string | null = null;
  private destroyed = false;
  /**
   * Cached booking created on first submit — reused on retries to avoid
   * duplicate booking rows + duplicate PaymentIntents when a network
   * flake causes the user to click "Pay & Confirm" again.
   */
  private bookingResult: CreateBookingResponse | null = null;
  /**
   * Snapshot of the inputs that produced `bookingResult`. If any of these
   * change (e.g. user backs out to edit dates / details), the cached
   * booking is invalidated on the next submit.
   */
  private bookingInputHash: string | null = null;

  constructor(host: HTMLElement, opts: PaymentStepOptions) {
    this.host = host;
    this.opts = opts;
    this.render();
    void this.initStripe();
  }

  destroy(): void {
    this.destroyed = true;
    try {
      this.paymentElement?.destroy();
    } catch {
      // ignore
    }
    this.host.innerHTML = '';
  }

  /** Called by the modal footer "Pay & Confirm" button. */
  async submit(): Promise<void> {
    if (this.submitting) return;
    if (!this.stripe || !this.elements || !this.paymentElement) {
      this.generalError = 'Payment form is still loading. Please wait a moment.';
      this.renderError();
      return;
    }
    this.submitting = true;
    this.generalError = null;
    this.renderError();
    this.renderSubmitting();

    // Wrap the entire flow in try/finally so an unexpected throw still
    // releases the submitting flag (footer button re-enables).
    try {
      // -------------------------------------------------------------
      // 1. Validate Payment Element fields locally before we hit the
      //    backend. This surfaces "missing card number" etc. without
      //    creating a wasted booking row.
      // -------------------------------------------------------------
      const submitResult = await this.elements.submit();
      if (submitResult.error) {
        this.paymentError = submitResult.error.message ?? 'Please complete your payment details.';
        this.renderPaymentError();
        return;
      }

      // -------------------------------------------------------------
      // 2. Booking creation (cached across retries when inputs match)
      // -------------------------------------------------------------
      const currentHash = this.computeInputHash();
      if (this.bookingResult && this.bookingInputHash !== currentHash) {
        // Inputs changed since we cached — must create a fresh booking.
        this.bookingResult = null;
        this.bookingInputHash = null;
      }

      let booking = this.bookingResult;
      if (!booking) {
        try {
          booking = await this.opts.api.createBooking({
            type: 'booking',
            check_in: this.isoDate(this.opts.calendar.checkIn),
            check_out: this.isoDate(this.opts.calendar.checkOut),
            num_adults: this.opts.calendar.numAdults,
            num_children: this.opts.calendar.numChildren,
            children_ages: this.opts.childAges,
            has_pets: this.opts.hasPets,
            first_name: this.opts.guest.firstName,
            last_name: this.opts.guest.lastName,
            email: this.opts.guest.email,
            phone: this.opts.guest.phone,
            notes: this.opts.guest.notes,
            gdpr_consent: this.opts.guest.gdprConsent,
          });
        } catch (e) {
          // 409 = dates taken since the guest chose them (concurrent booking
          // or an Airbnb sync that just landed). Bounce to the calendar step
          // rather than stranding them on the payment screen.
          if (e instanceof APIError && e.status === 409 && this.opts.onDatesUnavailable) {
            this.opts.onDatesUnavailable(e.message);
            return;
          }
          this.generalError =
            e instanceof APIError ? e.message : 'Could not create booking. Please try again.';
          this.renderError();
          return;
        }
        this.bookingResult = booking;
        this.bookingInputHash = currentHash;
      }

      if (!booking.stripeClientSecret) {
        this.generalError = 'Payment is not configured for this booking. Please contact us.';
        this.renderError();
        return;
      }

      // -------------------------------------------------------------
      // 3. Stripe payment confirmation. redirect: 'if_required' means
      //    cards + wallets (Apple Pay, Google Pay, Link) resolve inline
      //    with a paymentIntent result. Only exotic methods (Klarna,
      //    iDEAL etc.) would trigger a real redirect to return_url.
      // -------------------------------------------------------------
      let confirm;
      try {
        confirm = await this.stripe.confirmPayment({
          elements: this.elements,
          clientSecret: booking.stripeClientSecret,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                name: `${this.opts.guest.firstName} ${this.opts.guest.lastName}`.trim(),
                email: this.opts.guest.email,
                phone: this.opts.guest.phone,
              },
            },
          },
          redirect: 'if_required',
        });
      } catch (e) {
        this.generalError =
          e instanceof Error ? e.message : 'Payment failed. Please try again.';
        this.renderError();
        return;
      }

      if (confirm.error) {
        this.generalError = confirm.error.message ?? 'Payment failed. Please try again.';
        this.renderError();
        return;
      }

      if (!confirm.paymentIntent) {
        this.generalError = 'Payment failed. Please try again.';
        this.renderError();
        return;
      }

      const status = confirm.paymentIntent.status;

      // requires_action = 3-D Secure challenge was interrupted or the bank
      // wants another authentication step. Stripe.js already tried once; ask
      // the guest to retry so we get another shot at the challenge.
      if (status === 'requires_action' || status === 'requires_payment_method' || status === 'requires_confirmation') {
        this.generalError = 'Your card needs additional verification. Please try again — you may be asked to approve the payment in your banking app.';
        this.renderError();
        return;
      }

      if (status !== 'succeeded' && status !== 'processing' && status !== 'requires_capture') {
        this.generalError = 'Payment could not be completed. Please try again or contact us.';
        console.warn('[No4Widget] Unexpected PaymentIntent status:', status);
        this.renderError();
        return;
      }

      if (this.destroyed) return;
      this.opts.onSuccess(booking);
    } finally {
      this.submitting = false;
      // Restore the payment form's interactivity (mount opacity / pointer-events).
      if (!this.destroyed) this.renderSubmitting();
    }
  }

  /**
   * Hash the inputs that identify a "same booking" — if these change,
   * we must invalidate the cached bookingResult and create a fresh one.
   */
  private computeInputHash(): string {
    const c = this.opts.calendar;
    const g = this.opts.guest;
    return JSON.stringify({
      ci: c.checkIn ? this.isoDate(c.checkIn) : null,
      co: c.checkOut ? this.isoDate(c.checkOut) : null,
      a: c.numAdults,
      ch: c.numChildren,
      ages: this.opts.childAges,
      pets: this.opts.hasPets,
      fn: g.firstName,
      ln: g.lastName,
      em: g.email,
      ph: g.phone,
      nt: g.notes,
      gd: g.gdprConsent,
    });
  }

  isSubmitting(): boolean {
    return this.submitting;
  }

  /* ----------------------------------------------------------------- */

  private async initStripe(): Promise<void> {
    try {
      const StripeFn = await loadStripeJs();
      if (this.destroyed) return;
      this.stripe = StripeFn(this.opts.stripePublishableKey);

      // Payment Element requires an amount + currency upfront so it can
      // render wallets (Apple Pay / Google Pay) with the correct total.
      // Fall back to a nominal £1 if serverPrice is somehow missing —
      // elements.submit() will still gate on card validity.
      const depositPence = this.opts.calendar.serverPrice?.depositPence ?? 100;

      this.elements = this.stripe.elements({
        mode: 'payment',
        amount: depositPence,
        currency: 'gbp',
        locale: 'en-GB',
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#1c2421',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSizeBase: '16px',
          },
        },
      });

      // fields.billingDetails = 'never' means Stripe collects no billing
      // details from the guest — we already have name/email/phone from
      // the guest form and pass them via confirmParams.payment_method_data.
      // This is what suppresses the ZIP/postcode prompt.
      this.paymentElement = this.elements.create('payment', {
        layout: 'tabs',
        defaultValues: {
          billingDetails: {
            name: `${this.opts.guest.firstName} ${this.opts.guest.lastName}`.trim(),
            email: this.opts.guest.email,
            phone: this.opts.guest.phone,
          },
        },
        fields: {
          billingDetails: 'never',
        },
      });

      const mount = this.host.querySelector<HTMLElement>('[data-role="stripe-mount"]');
      if (!mount) return;
      this.paymentElement.mount(mount);
      this.paymentElement.on('change', (ev) => {
        this.paymentError = ev.error?.message ?? null;
        this.renderPaymentError();
      });
    } catch (e) {
      this.generalError =
        e instanceof Error ? e.message : 'Could not initialise payment form.';
      this.renderError();
    }
  }

  private render(): void {
    const c = this.opts.calendar;
    const ci = c.checkIn;
    const co = c.checkOut;
    const nights = ci && co ? diffDays(ci, co) : 0;
    const price = c.serverPrice;
    this.host.innerHTML = `
      <div class="no4w-summary">
        <h3>Booking summary</h3>
        <dl>
          <dt>Check-in</dt>
          <dd>${ci ? escapeHTML(formatHumanDate(ci)) : '—'}</dd>
          <dt>Check-out</dt>
          <dd>${co ? escapeHTML(formatHumanDate(co)) : '—'}</dd>
          <dt>Nights</dt>
          <dd>${nights}</dd>
          <dt>Guests</dt>
          <dd>${c.numAdults} adult${c.numAdults === 1 ? '' : 's'}${
            c.numChildren > 0 ? `, ${c.numChildren} child${c.numChildren === 1 ? '' : 'ren'}` : ''
          }${c.hasPets ? ', + pets' : ''}</dd>
          ${
            price
              ? (() => {
                  let balanceDueHtml = '';
                  if (price.balanceDueDate) {
                    const parsed = fromISODate(price.balanceDueDate);
                    if (parsed) {
                      balanceDueHtml = `<span class="no4w-hint">due ${escapeHTML(
                        formatLongDate(parsed),
                      )}</span>`;
                    }
                  }
                  return `
                <dt>Total</dt>
                <dd>${formatPence(price.totalPricePence)}</dd>
                <dt>Deposit today</dt>
                <dd>${formatPence(price.depositPence)}</dd>
                <dt>Balance</dt>
                <dd>${formatPence(price.balancePence)} ${balanceDueHtml}</dd>
              `;
                })()
              : ''
          }
        </dl>
      </div>

      <div class="no4w-field">
        <label>Payment details</label>
        <div class="no4w-stripe-mount" data-role="stripe-mount">
          <p class="no4w-hint" style="margin:0;">
            <span class="no4w-loader no4w-loader--dark"></span> Loading secure payment form…
          </p>
        </div>
        <p class="no4w-stripe-error" data-role="payment-error" aria-live="polite"></p>
      </div>

      <div data-role="general-error"></div>

      <p class="no4w-hint">
        Your card is processed securely by Stripe. You will be charged
        ${
          price
            ? formatPence(price.depositPence) + ' as a non-refundable deposit'
            : 'the deposit amount shown above'
        }${
          (() => {
            if (!price?.balanceDueDate) return '. The balance is due before arrival';
            const parsed = fromISODate(price.balanceDueDate);
            return parsed
              ? '. The balance is due by ' + escapeHTML(formatLongDate(parsed))
              : '. The balance is due before arrival';
          })()
        }.
      </p>
    `;
  }

  private renderPaymentError(): void {
    const el = this.host.querySelector<HTMLElement>('[data-role="payment-error"]');
    if (el) el.textContent = this.paymentError ?? '';
  }

  private renderError(): void {
    const el = this.host.querySelector<HTMLElement>('[data-role="general-error"]');
    if (!el) return;
    if (this.generalError) {
      el.innerHTML = `<div class="no4w-banner no4w-banner--error">${escapeHTML(
        this.generalError,
      )}</div>`;
    } else {
      el.innerHTML = '';
    }
  }

  private renderSubmitting(): void {
    // The footer button shows its own loader; payment form itself is left
    // mostly intact so the user can re-try without losing card state.
    const mount = this.host.querySelector<HTMLElement>('[data-role="stripe-mount"]');
    if (!mount) return;
    if (this.submitting) {
      mount.style.opacity = '0.7';
      mount.style.pointerEvents = 'none';
    } else {
      mount.style.opacity = '';
      mount.style.pointerEvents = '';
    }
  }

  private isoDate(d: Date | null): string {
    if (!d) throw new Error('Missing date');
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
